import React, { useState } from "react";
import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import api from "../lib/api";
import { Sparkle, ArrowRight, Heart, Briefcase, Pulse } from "@phosphor-icons/react";
import { toast } from "sonner";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import PremiumTeaser from "../components/PremiumTeaser";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
const fmtAgeBand = (start, end) => {
  const ys = (new Date(end) - new Date(start)) / (365.25 * 86400 * 1000);
  return ys >= 1 ? `${ys.toFixed(1)} yrs` : `${Math.round(ys * 12)} mo`;
};

// Lens labels for the 3 themes
const LENSES = [
  { id: "overall",      icon: Sparkle,   label: "Overall (D1)",         color: "amber"   },
  { id: "relationship", icon: Heart,     label: "Relationship (D9)",    color: "rose"    },
  { id: "career",       icon: Briefcase, label: "Career (D10)",         color: "indigo"  },
];

const Ribbon = ({ blocks, currentIdx, onSelect, label }) => {
  // Render a horizontal proportional ribbon — each block coloured by planet
  const total = blocks.reduce(
    (s, b) => s + (new Date(b.end) - new Date(b.start)),
    0
  );
  return (
    <div className="space-y-2">
      <div className="label-eyebrow text-muted">{label}</div>
      <div className="flex w-full h-10 rounded-lg overflow-hidden border border-soft" data-testid="dasha-ribbon">
        {blocks.map((b, i) => {
          const w = ((new Date(b.end) - new Date(b.start)) / total) * 100;
          const isCurrent = i === currentIdx;
          const isPast = b.is_past;
          return (
            <button
              key={`${b.lord}-${b.start}`}
              onClick={() => onSelect(i)}
              style={{ width: `${w}%`, backgroundColor: b.color, opacity: isPast ? 0.35 : 1 }}
              className={`relative text-[10px] text-white font-medium flex items-center justify-center transition-all hover:opacity-90 ${
                isCurrent ? "ring-2 ring-black ring-offset-1" : ""
              }`}
              title={`${b.lord} · ${fmtDate(b.start)} → ${fmtDate(b.end)}`}
              data-testid={`ribbon-block-${i}`}
            >
              {w > 4 && <span className="px-1 truncate">{b.lord[0]}</span>}
              {isCurrent && (
                <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-rose-500 ring-2 ring-white" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const LensCard = ({ lens, flavour, placement, houseTopic, lord }) => {
  const Icon = lens.icon;
  return (
    <div className={`rounded-2xl p-5 sm:p-6 bg-${lens.color}-50 border border-${lens.color}-200`} data-testid={`lens-${lens.id}`}>
      <div className="flex items-center gap-2 label-eyebrow">
        <Icon size={16} weight="duotone" /> {lens.label}
      </div>
      <p className={`font-serif-display text-xl sm:text-2xl mt-3 font-light leading-snug text-${lens.color}-950`}>{flavour}</p>
      {placement?.rashi && (
        <div className={`mt-4 pt-4 border-t border-${lens.color}-200 text-xs space-y-1.5`}>
          <div>
            <span className="text-muted">{lord} placed in: </span>
            <span className="font-medium">H{placement.house} {placement.rashi}</span>
            {placement.dignity && placement.dignity !== "neutral" && (
              <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                placement.dignity === "exalted" ? "bg-emerald-100 text-emerald-800" :
                placement.dignity === "debilitated" ? "bg-rose-100 text-rose-800" :
                "bg-amber-100 text-amber-800"
              }`}>{placement.dignity}</span>
            )}
            {placement.retrograde && <span className="ml-2 text-rose-700 text-[10px] font-medium">(R)</span>}
          </div>
          {houseTopic?.[0] && (
            <div>
              <span className="text-muted">House theme: </span>
              <span className="font-medium">{houseTopic[0]}</span>
            </div>
          )}
          {houseTopic?.[1] && <div className="text-muted text-[11px] leading-snug pt-1">{houseTopic[1]}</div>}
        </div>
      )}
    </div>
  );
};

export default function DashaCalculatorPage() {
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  const [placeQuery, setPlaceQuery] = useState("");
  usePrefillBirthDetails(setForm, setPlaceQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [adIdx, setAdIdx] = useState(0);
  const [pdIdx, setPdIdx] = useState(0);
  // "time-machine" filter — null = current date
  const [travelDate, setTravelDate] = useState(null);  // ISO YYYY-MM-DD string
  const [travelLoading, setTravelLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    const place = form.place || placeQuery;
    if (!form.date || !form.time || !place) {
      toast.error("Please fill date, time and place");
      return;
    }
    setLoading(true);
    setTravelDate(null);
    try {
      const r = await api.post("/dasha/compute", { ...form, place });
      setData(r.data);
      setAdIdx(r.data.current_ad_idx);
      setPdIdx(r.data.current_pd_idx);
      setTimeout(() => document.querySelector("[data-testid='dasha-result']")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to compute dashas");
    } finally {
      setLoading(false);
    }
  };

  // Time-machine: re-fetch for a specific YYYY-MM-DD
  const loadForDate = async (targetDate) => {
    if (!data) return;
    const place = form.place || placeQuery;
    setTravelLoading(true);
    try {
      const r = await api.post("/dasha/compute", { ...form, place, at_date: targetDate });
      setData(r.data);
      setAdIdx(r.data.current_ad_idx);
      setPdIdx(r.data.current_pd_idx);
      setTravelDate(targetDate);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to time-travel");
    } finally {
      setTravelLoading(false);
    }
  };

  const md = data?.mahadashas?.[data.current_md_idx];
  const ad = data?.antardashas_current_md?.[adIdx];
  const pd = data?.pratyantardashas_current_ad?.[pdIdx];

  return (
    <PageShell>
      <SEO
        title="Vimshottari Dasha Calculator — Free Mahadasha, Antardasha, Pratyantardasha"
        description="Free Vimshottari Dasha calculator. Reads Mahadasha → Antardasha → Pratyantardasha with placements in D1, D9 (Navamsa) and D10 (Dasamsa) — 81 unique flavour readings for marriage, career and life events."
        path="/dashas"
        keywords={[
          "vimshottari dasha calculator", "dasha calculator", "free dasha calculator",
          "mahadasha calculator", "antardasha calculator", "pratyantar dasha",
          "current dasha", "vedic dasha", "dasha period predictions",
          "saturn dasha", "rahu dasha", "jupiter dasha", "venus dasha",
          "sade sati period", "dasha bhukti chart",
        ]}
      />
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20">
        <header className="mb-10">
          <div className="label-eyebrow text-muted">Vimshottari · Dasha Calculator</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">Your dasha — decoded in three lifetimes of detail</h1>
          <p className="text-muted text-sm sm:text-base mt-4 font-light max-w-2xl">
            Reads Vimshottari Mahadasha → Antardasha → Pratyantardasha. For every lord, surfaces the placement in your D1 (overall), D9 (relationships), and D10 (career) charts — with 81 unique flavour readings that no other tool gives.
          </p>
        </header>

        <form onSubmit={submit} className="rounded-2xl p-6 sm:p-7 bg-[#FAF4E8] border border-soft space-y-5 max-w-2xl" data-testid="dasha-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1">
              <span className="text-xs text-muted">Date of birth</span>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="dasha-date" required />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted">Time of birth</span>
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="dasha-time" required />
            </label>
          </div>
          <div className="relative">
            <label className="space-y-1 block">
              <span className="text-xs text-muted">Place of birth</span>
              <input
                type="text"
                value={placeQuery}
                onChange={async (e) => {
                  const q = e.target.value;
                  setPlaceQuery(q);
                  if (q.trim().length >= 2) {
                    setSearching(true);
                    try {
                      const { data } = await api.get("/places/search", { params: { q: q.trim() } });
                      setSuggestions(data || []);
                    } catch { /* ignore */ }
                    finally { setSearching(false); }
                  } else {
                    setSuggestions([]);
                  }
                }}
                placeholder="Start typing a city — e.g. Mumbai, Delhi, London..."
                className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                data-testid="dasha-place"
              />
            </label>
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
                {suggestions.map((s, i) => (
                  <button
                    type="button"
                    key={i}
                    onClick={() => {
                      const label = s.name || s.label;
                      setForm({ ...form, place: label, timezone: s.timezone || "Asia/Kolkata" });
                      setPlaceQuery(label);
                      setSuggestions([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-soft last:border-0"
                    data-testid={`dasha-place-option-${i}`}
                  >
                    {s.name || s.label}
                  </button>
                ))}
              </div>
            )}
            {searching && <div className="text-xs text-muted mt-1">Searching…</div>}
          </div>
          <button type="submit" disabled={loading} className="btn-primary rounded-full px-7 py-3 font-medium disabled:opacity-50" data-testid="dasha-submit">
            {loading ? "Computing…" : <>Compute my dasha <ArrowRight size={16} className="inline ml-1" /></>}
          </button>
        </form>

        {data && (
          <div className="mt-12 space-y-10" data-testid="dasha-result">
            <div className="rounded-2xl p-6 sm:p-7 bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="label-eyebrow text-[#7C2D12]">
                    {travelDate ? `Period on ${new Date(travelDate + "T12:00").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}` : "Current period"}
                  </div>
                  <h2 className="font-serif-display text-3xl sm:text-4xl mt-2 font-light leading-tight" data-testid="current-period-label">
                    {data.current_period_label}
                  </h2>
                </div>
                {travelDate && (
                  <button
                    onClick={() => loadForDate(null) || loadForDate(new Date().toISOString().slice(0, 10))}
                    className="btn-ghost rounded-full px-4 py-1.5 text-xs font-medium border"
                    data-testid="back-to-today"
                    disabled={travelLoading}
                  >
                    ← Back to today
                  </button>
                )}
              </div>
              <p className="text-sm text-muted mt-3 font-light">
                You {travelDate ? "will be" : "are"} in <b>{md.lord}</b> mahadasha ({fmtDate(md.start)} → {fmtDate(md.end)}, {md.years} yrs total). Sub-period: <b>{ad.lord}</b> ({fmtDate(ad.start)} → {fmtDate(ad.end)}). Pratyantar: <b>{pd.lord}</b>.
              </p>
            </div>

            {/* === AUTHOR'S RESEARCH-BACKED DASHA INSIGHTS === */}
            {data.research_insights && (
              (data.research_insights.current?.length > 0 ||
               data.research_insights.upcoming?.length > 0 ||
               data.research_insights.lagna_specific?.length > 0) && (
                <section className="space-y-5" data-testid="research-insights">
                  <div className="label-eyebrow text-muted flex items-center gap-2">
                    <Sparkle size={14} weight="duotone" /> Research-backed dasha signals · personal observations from 200+ business / wealth / loss charts
                  </div>

                  {/* CURRENT signals */}
                  {data.research_insights.current?.length > 0 && (
                    <div className="space-y-3">
                      <div className="text-xs text-amber-900 font-bold tracking-wider">FIRING NOW</div>
                      {data.research_insights.current.map((s, i) => (
                        <div key={i} className="rounded-2xl p-5 bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-300" data-testid={`research-current-${i}`}>
                          <div className="text-[10px] font-bold tracking-wider text-amber-900">{s.period_label}</div>
                          <p className="text-sm mt-2 font-light leading-snug text-[#3F2D1B]">{s.body}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* UPCOMING signals — within next 12 months only */}
                  {data.research_insights.upcoming?.length > 0 && (
                    <div className="space-y-3">
                      <div className="text-xs text-indigo-900 font-bold tracking-wider">UPCOMING IN THE NEXT 12 MONTHS</div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {data.research_insights.upcoming.map((s, i) => (
                          <div key={i} className="rounded-2xl p-4 bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-200" data-testid={`research-upcoming-${i}`}>
                            <div className="flex items-baseline justify-between gap-2">
                              <div className="text-[10px] font-bold tracking-wider text-indigo-900">{s.period_label}</div>
                              <div className="text-[10px] text-muted">{fmtDate(s.start)} → {fmtDate(s.end)}</div>
                            </div>
                            <p className="text-sm mt-2 font-light leading-snug text-[#3F2D1B]">{s.body}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* LAGNA-specific notes */}
                  {data.research_insights.lagna_specific?.length > 0 && (
                    <div className="space-y-3">
                      <div className="text-xs text-emerald-900 font-bold tracking-wider">CHART-SPECIFIC NOTES</div>
                      {data.research_insights.lagna_specific.map((s, i) => {
                        const isWarn = s.kind?.includes("warning");
                        const skin = isWarn
                          ? "bg-rose-50 border-rose-200 text-rose-950"
                          : "bg-emerald-50 border-emerald-200 text-emerald-950";
                        return (
                          <div key={i} className={`rounded-2xl p-4 border ${skin}`} data-testid={`research-lagna-${i}`}>
                            <p className="text-sm font-light leading-snug">{s.body}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )
            )}

            {/* TIME MACHINE — peek any month/year */}
            <section className="rounded-2xl p-5 sm:p-6 bg-[#FAF4E8] border border-soft" data-testid="time-machine">
              <div className="flex items-center gap-2 label-eyebrow text-muted">
                <Sparkle size={14} weight="duotone" /> Peek any month — see which dashas will fire & how to ride them
              </div>
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <select
                  value={travelDate ? travelDate.slice(0, 4) : new Date().getFullYear()}
                  onChange={(e) => {
                    const y = e.target.value;
                    const m = travelDate ? travelDate.slice(5, 7) : String(new Date().getMonth() + 1).padStart(2, "0");
                    loadForDate(`${y}-${m}-15`);
                  }}
                  className="px-3 py-2 rounded-md border border-soft bg-white text-sm"
                  disabled={travelLoading}
                  data-testid="travel-year"
                >
                  {(() => {
                    const start = new Date(md.start).getFullYear() < new Date(form.date).getFullYear()
                      ? new Date(form.date).getFullYear()
                      : new Date(form.date).getFullYear();
                    const end = new Date(data.mahadashas[data.mahadashas.length - 1].end).getFullYear();
                    const years = [];
                    for (let y = start; y <= end; y++) years.push(y);
                    return years.map((y) => <option key={y} value={y}>{y}</option>);
                  })()}
                </select>
                <select
                  value={travelDate ? travelDate.slice(5, 7) : String(new Date().getMonth() + 1).padStart(2, "0")}
                  onChange={(e) => {
                    const m = e.target.value;
                    const y = travelDate ? travelDate.slice(0, 4) : new Date().getFullYear();
                    loadForDate(`${y}-${m}-15`);
                  }}
                  className="px-3 py-2 rounded-md border border-soft bg-white text-sm"
                  disabled={travelLoading}
                  data-testid="travel-month"
                >
                  {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                    <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>
                  ))}
                </select>
                {travelLoading && <span className="text-xs text-muted">Loading…</span>}
                {travelDate && (
                  <button
                    onClick={() => loadForDate(new Date().toISOString().slice(0, 10))}
                    className="text-xs text-amber-700 underline ml-auto"
                    data-testid="reset-to-current"
                  >
                    Reset to current
                  </button>
                )}
              </div>
              <p className="text-xs text-muted mt-3 font-light leading-snug">
                Move the year/month dropdowns — the entire reading below (manifest, precautions, three-lens flavours, ribbons) re-renders to show what dashas you&apos;ll be in then.
              </p>
            </section>

            {/* === MAHADASHA RIBBON === */}
            <section className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="label-eyebrow text-muted">120-year Mahadasha timeline · click a block to inspect</div>
                <div className="text-xs text-muted">🔴 ring = current</div>
              </div>
              <Ribbon blocks={data.mahadashas} currentIdx={data.current_md_idx} onSelect={() => {}} label="Mahadasha" />
              <div className="flex flex-wrap gap-2 text-[10px] text-muted">
                {data.mahadashas.map((m, i) => (
                  <span key={i} className="inline-flex items-center gap-1">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: m.color }} />
                    {m.lord} {fmtDate(m.start).slice(-4)} – {fmtDate(m.end).slice(-4)}
                  </span>
                ))}
              </div>
            </section>

            {/* === CURRENT MAHADASHA · 3-LENS === */}
            <section className="space-y-5">
              <div className="label-eyebrow text-muted">Mahadasha — three-lens reading</div>
              <div className="rounded-2xl p-5 bg-white border border-soft">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <div className="font-serif-display text-2xl sm:text-3xl font-light" style={{ color: md.color }}>
                    {md.lord} mahadasha
                  </div>
                  <div className="text-xs text-muted">{fmtDate(md.start)} → {fmtDate(md.end)} · {md.years} years</div>
                </div>
                <p className="text-sm text-muted mt-2 font-light"><b>{md.karaka_short}</b> — {md.karaka_long}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {LENSES.map((lens) => {
                  const placement = lens.id === "overall" ? md.d1 : lens.id === "relationship" ? md.d9 : md.d10;
                  const houseTopic = lens.id === "overall" ? md.house_overall : lens.id === "relationship" ? md.house_relationship : md.house_career;
                  const flavour = lens.id === "overall"
                    ? `${md.karaka_long} The decade's overarching mood is ${md.lord}'s signature.`
                    : lens.id === "relationship"
                    ? `In love and partnerships, ${md.lord} colours ${houseTopic?.[0] || "this area"} of your life. Watch how the people closest reflect ${md.lord}'s nature.`
                    : `Career and public role take on ${md.lord}'s shape — industries linked to ${md.karaka_short.split(" ·")[0].toLowerCase()} rise or recede.`;
                  return (
                    <LensCard key={lens.id} lens={lens} flavour={flavour} placement={placement} houseTopic={houseTopic} lord={md.lord} />
                  );
                })}
              </div>
            </section>

            {/* === ANTARDASHA RIBBON === */}
            <section className="space-y-4">
              <div className="label-eyebrow text-muted">Antardashas inside {md.lord} mahadasha · click to inspect</div>
              <Ribbon blocks={data.antardashas_current_md} currentIdx={adIdx} onSelect={setAdIdx} label={`${md.lord} → ___`} />
              <div className="text-xs text-muted">
                Selected: <b>{ad.lord}</b> ({fmtDate(ad.start)} → {fmtDate(ad.end)} · {fmtAgeBand(ad.start, ad.end)})
              </div>
            </section>

            {/* === ANTARDASHA · 3-LENS FLAVOUR === */}
            <section className="space-y-5">
              <div className="label-eyebrow text-muted">{md.lord} → {ad.lord} · three-lens flavour</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {LENSES.map((lens) => {
                  const flavour = lens.id === "overall" ? ad.flavour_overall : lens.id === "relationship" ? ad.flavour_relationship : ad.flavour_career;
                  const placement = lens.id === "overall" ? ad.d1 : lens.id === "relationship" ? ad.d9 : ad.d10;
                  const houseTopic = lens.id === "overall" ? ad.house_overall : lens.id === "relationship" ? ad.house_relationship : ad.house_career;
                  return (
                    <LensCard key={lens.id} lens={lens} flavour={flavour} placement={placement} houseTopic={houseTopic} lord={ad.lord} />
                  );
                })}
              </div>
            </section>

            {/* === MANIFEST + PRECAUTIONS ===
                What to DO, what to AVOID — for current MD and AD lords */}
            <section className="space-y-5" data-testid="dasha-manifest-section">
              <div className="label-eyebrow text-muted">How to ride this dasha · play & precaution</div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Mahadasha play */}
                <div className="rounded-2xl p-5 sm:p-6 bg-emerald-50 border border-emerald-200" data-testid="manifest-md-play">
                  <div className="flex items-center gap-2 label-eyebrow text-emerald-900">
                    <Sparkle size={16} weight="duotone" /> {md.lord} Mahadasha · what to manifest
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {md.manifest.map((m, i) => (
                      <li key={i} className="flex gap-2.5 text-sm leading-snug">
                        <span className="text-emerald-700 mt-1 shrink-0">▸</span>
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Mahadasha precautions */}
                <div className="rounded-2xl p-5 sm:p-6 bg-rose-50 border border-rose-200" data-testid="manifest-md-care">
                  <div className="flex items-center gap-2 label-eyebrow text-rose-900">
                    <Pulse size={16} weight="duotone" /> {md.lord} Mahadasha · precautions
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {md.precautions.map((p, i) => (
                      <li key={i} className="flex gap-2.5 text-sm leading-snug">
                        <span className="text-rose-700 mt-1 shrink-0">⚠</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Antardasha play */}
                <div className="rounded-2xl p-5 sm:p-6 bg-emerald-50/60 border border-emerald-200" data-testid="manifest-ad-play">
                  <div className="flex items-center gap-2 label-eyebrow text-emerald-900">
                    <Sparkle size={16} weight="duotone" /> {ad.lord} Antardasha · how to ride this sub-period
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {ad.manifest.map((m, i) => (
                      <li key={i} className="flex gap-2.5 text-sm leading-snug">
                        <span className="text-emerald-700 mt-1 shrink-0">▸</span>
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* Antardasha precautions */}
                <div className="rounded-2xl p-5 sm:p-6 bg-rose-50/60 border border-rose-200" data-testid="manifest-ad-care">
                  <div className="flex items-center gap-2 label-eyebrow text-rose-900">
                    <Pulse size={16} weight="duotone" /> {ad.lord} Antardasha · precautions
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {ad.precautions.map((p, i) => (
                      <li key={i} className="flex gap-2.5 text-sm leading-snug">
                        <span className="text-rose-700 mt-1 shrink-0">⚠</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* === PRATYANTAR RIBBON === */}
            <section className="space-y-4">
              <div className="label-eyebrow text-muted">Pratyantardashas inside {ad.lord} antardasha · click to inspect</div>
              <Ribbon blocks={data.pratyantardashas_current_ad.map((p) => ({ ...p, is_past: new Date(p.end) < new Date() }))} currentIdx={pdIdx} onSelect={setPdIdx} label={`${md.lord} → ${ad.lord} → ___`} />
              <div className="text-xs text-muted">
                Selected: <b>{pd.lord}</b> ({fmtDate(pd.start)} → {fmtDate(pd.end)})
              </div>
              <div className="rounded-2xl p-5 bg-[#FAF4E8] border border-soft">
                <p className="font-serif-display text-xl font-light leading-snug">
                  <span style={{ color: pd.color }}>{pd.lord}</span> pratyantar inside <span style={{ color: ad.color }}>{ad.lord}</span> antar inside <span style={{ color: md.color }}>{md.lord}</span> mahadasha — these three planets weave the daily texture of your life during this window.
                </p>
                <div className="flex items-center gap-3 mt-4 text-xs text-muted">
                  <Pulse size={14} weight="duotone" />
                  {md.lord}-{ad.lord}-{pd.lord} = {md.karaka_short.split(" ·")[0]} × {ad.lord === md.lord ? "intensified" : "tempered by"} × {pd.lord === ad.lord ? "doubling-down" : pd.lord + " sub-current"}
                </div>
              </div>
              {/* Pratyantar play + precautions — same deterministic per-lord
                  playbooks as MD/AD, applied to the selected micro-period */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="rounded-2xl p-5 sm:p-6 bg-emerald-50/40 border border-emerald-200" data-testid="manifest-pd-play">
                  <div className="flex items-center gap-2 label-eyebrow text-emerald-900">
                    <Sparkle size={16} weight="duotone" /> {pd.lord} Pratyantar · how to ride this micro-period
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {(pd.manifest || []).map((m, i) => (
                      <li key={i} className="flex gap-2.5 text-sm leading-snug">
                        <span className="text-emerald-700 mt-1 shrink-0">▸</span>
                        <span>{m}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-2xl p-5 sm:p-6 bg-rose-50/40 border border-rose-200" data-testid="manifest-pd-care">
                  <div className="flex items-center gap-2 label-eyebrow text-rose-900">
                    <Pulse size={16} weight="duotone" /> {pd.lord} Pratyantar · precautions
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {(pd.precautions || []).map((p, i) => (
                      <li key={i} className="flex gap-2.5 text-sm leading-snug">
                        <span className="text-rose-700 mt-1 shrink-0">⚠</span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Funnel teaser — dasha users → 20-Year Report */}
            <PremiumTeaser
              testid="dasha-premium-teaser"
              topLine={<>
                You&apos;ve seen the ladder.<br/>
                <span className="text-amber-300">Now see every rung, year-by-year.</span>
              </>}
              subLine={<>
                Your current <b className="text-amber-100">{md.lord}-{ad.lord}</b> period is one chapter.
                The full 55+ page 20-Year Report decodes each dasha with year-by-year career, marriage, wealth &amp;
                health scores, plus Sade Sati windows, 6 doshas and personalised gem &amp; rudraksha remedies.
              </>}
            />

            {/* CTA */}
            <ToolAstrologerCta
              requiredTechnique="Bhrigu Nandi Nadi"
              pitch={`Read your current ${md.lord}-${ad.lord} period live — including exact transit triggers & remedies`}
              ctaLabel="Talk to"
            />
          </div>
        )}
      </div>
    </PageShell>
  );
}
